// @file Whiteboard tools helpers
import type { ContentPickerSource, ContentSource } from '@@/bits/content_picker'
import { getContentSources } from '@@/bits/content_picker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import type { WhiteboardTool } from '@@/types/whiteboard'

interface WhiteboardToolSetting {
  id: string
  text: string
  ariaLabel: string
  icon: string
}

interface WhiteboardToolSettingEntry extends WhiteboardToolSetting {
  isEnabled: boolean
  iconColor?: ContentSource['color']
}

const TLDRAW_TOOLS_IN_SETTINGS: WhiteboardToolSetting[] = [
  {
    id: 'laser',
    text: __('Pointer'),
    ariaLabel: __('Toggle ‘Pointer’ tool'),
    icon: 'laser_pointer',
  },
  {
    id: 'draw-nested',
    text: __('Draw'),
    ariaLabel: __('Toggle ‘Draw’ tool'),
    icon: 'swiggly_line',
  },
  {
    id: 'text',
    text: __('Text'),
    ariaLabel: __('Toggle ‘Text’ tool'),
    icon: 'text',
  },
  {
    id: 'note',
    text: __('Note'),
    ariaLabel: __('Toggle ‘Note’ tool'),
    icon: 'postnote',
  },
  {
    id: 'geo',
    text: __('Shape'),
    ariaLabel: __('Toggle ‘Shape’ tool'),
    icon: 'geo_star',
  },
  isAppUsing('sandboxConnector')
    ? {
        id: 'arrow',
        text: __('Connector'),
        ariaLabel: __('Toggle ‘Connector’ tool'),
        icon: 'solid_single_up_right',
      }
    : null,
].filter(Boolean) as WhiteboardToolSetting[]

const TLDRAW_TOOL_IDS = TLDRAW_TOOLS_IN_SETTINGS.map((tool) => tool.id)

const PADLET_TOOL_IDS_WITHOUT_PREFIX = getContentSources().map((s) => s.type)

/**
 * Converts a tool ID to the format stored in the settings.
 * Specifically, we prepend `padlet-` to the tool ID if it is a Padlet tool
 * so that there's less of a chance of collision with TLDraw tool IDs.
 */
const toStoredToolId = (toolId: string): string => {
  let storedToolId = toolId
  if (PADLET_TOOL_IDS_WITHOUT_PREFIX.includes(toolId as ContentPickerSource)) {
    storedToolId = `padlet-${toolId}`
  }
  return storedToolId
}

const PADLET_TOOL_IDS = PADLET_TOOL_IDS_WITHOUT_PREFIX.map((id) => toStoredToolId(id))

/**
 * These tools are shown in the toolbar so we must map them from `getContentSources`
 * which already filters out unavailable tools (e.g. if the device doesn't support
 * screen recording).
 */
const AVAILABLE_PADLET_TOOLS: WhiteboardTool[] = getContentSources().map((s) => ({
  id: toStoredToolId(s.type),
  label: s.text,
  iconName: s.icon,
  tldrawToolId: '', // Padlet tools don't have tldrawToolId
}))

const isPadletTool = (toolId: string): boolean => {
  return PADLET_TOOL_IDS.includes(toolId)
}

/**
 * Converts a tool ID to a `ContentPickerSource`.
 * Returns `null` if the tool ID is not a Padlet tool.
 */
const toContentSource = (toolId: string): ContentPickerSource | null => {
  if (!isPadletTool(toolId)) return null
  return toolId.replace('padlet-', '') as ContentPickerSource
}

export {
  AVAILABLE_PADLET_TOOLS,
  isPadletTool,
  PADLET_TOOL_IDS,
  PADLET_TOOL_IDS_WITHOUT_PREFIX,
  TLDRAW_TOOL_IDS,
  TLDRAW_TOOLS_IN_SETTINGS,
  toContentSource,
  toStoredToolId,
}
export type { WhiteboardToolSettingEntry }
